import React, { FormEvent, useState } from 'react';
import { ArrowRight, CalendarClock, FilePlus2, FolderOpen, Plus, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import SessionGate from './SessionGate';
import { engineApiClient, EngineApiError } from '../shared/api/engineApiClient';
import { DocumentRole, DocumentStatus } from '../shared/api/types';

const roleLabels: Record<DocumentRole, string> = {
  OWNER: '所有者',
  EDITOR: '协作者',
  REVIEWER: '审核人',
  VIEWER: '查看者',
};

const statusLabels: Record<DocumentStatus, string> = {
  draft: '草稿',
  in_review: '审核中',
  changes_requested: '待修改',
  confirmed: '已确认',
};

const formatUpdatedAt = (value: string) => new Intl.DateTimeFormat('zh-CN', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(value));

const DocumentListContent: React.FC = () => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [createError, setCreateError] = useState<string>();
  const { data, error, isLoading, mutate } = useSWR(
    'engine:documents:first-page',
    () => engineApiClient.listDocuments(),
    { revalidateOnFocus: false },
  );

  const createDocument = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedTitle = title.trim();
    if (!normalizedTitle || isCreating) return;
    setIsCreating(true);
    setCreateError(undefined);
    try {
      const document = await engineApiClient.createDocument(normalizedTitle);
      await mutate();
      navigate(`/documents/${document.id}`);
    } catch (requestError) {
      setCreateError(requestError instanceof Error ? requestError.message : '创建失败，请重试');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="engine-app-page">
      <section className="engine-app-page-heading">
        <div>
          <p className="engine-app-eyebrow">Document workspace</p>
          <h1>交付文档</h1>
          <p>把脚本、画面与反馈组织在同一个可追溯的交付空间。</p>
        </div>
        <form className="engine-app-create-form" onSubmit={createDocument}>
          <label htmlFor="engine-new-document-title">新建文档</label>
          <div>
            <input
              id="engine-new-document-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={200}
              placeholder="例如：秋季品牌片交付"
              disabled={isCreating}
            />
            <button type="submit" className="engine-app-primary-button" disabled={!title.trim() || isCreating}>
              {isCreating ? <RefreshCw className="engine-app-spin" size={16} /> : <Plus size={16} />}
              创建
            </button>
          </div>
          {createError ? <span role="alert">{createError}</span> : null}
        </form>
      </section>

      <section className="engine-app-document-section" aria-labelledby="recent-documents-heading">
        <div className="engine-app-section-heading">
          <h2 id="recent-documents-heading">最近更新</h2>
          {data?.items.length ? <span>{data.items.length} 份文档</span> : null}
        </div>

        {isLoading ? (
          <div className="engine-app-document-list" aria-label="正在加载文档">
            {[0, 1, 2].map((item) => <div className="engine-app-document-skeleton" key={item} />)}
          </div>
        ) : error ? (
          <div className="engine-app-error-state" role="alert">
            <strong>文档暂时没有加载成功</strong>
            <p>{error instanceof EngineApiError && error.requestId ? `请求编号：${error.requestId}` : '请检查网络后重试。'}</p>
            <button type="button" className="engine-app-secondary-button" onClick={() => void mutate()}>
              <RefreshCw size={15} />重新加载
            </button>
          </div>
        ) : !data?.items.length ? (
          <div className="engine-app-empty-state">
            <div><FolderOpen size={28} /></div>
            <h2>从第一份交付文档开始</h2>
            <p>先创建文档，稍后可以在里面编排内容、邀请审核人并保留每次提交记录。</p>
            <button type="button" className="engine-app-secondary-button" onClick={() => document.getElementById('engine-new-document-title')?.focus()}>
              <FilePlus2 size={16} />填写文档名称
            </button>
          </div>
        ) : (
          <div className="engine-app-document-list">
            {data.items.map((document) => (
              <button
                type="button"
                className="engine-app-document-row"
                key={document.id}
                onClick={() => navigate(`/documents/${document.id}`)}
              >
                <span className="engine-app-document-icon">{document.title.slice(0, 1).toUpperCase()}</span>
                <span className="engine-app-document-copy">
                  <strong>{document.title}</strong>
                  <small><CalendarClock size={13} />{formatUpdatedAt(document.updatedAt)}</small>
                </span>
                <span className={`engine-app-status is-${document.status}`}>{statusLabels[document.status]}</span>
                <span className="engine-app-role">{roleLabels[document.role]}</span>
                <ArrowRight className="engine-app-row-arrow" size={17} />
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const DocumentListPage: React.FC = () => (
  <SessionGate>
    <DocumentListContent />
  </SessionGate>
);

export default DocumentListPage;

